import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

interface DemoModeContextType {
  isDemoMode: boolean;
  setIsDemoMode: (value: boolean) => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

export const DemoModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get the initial state from localStorage or default to true (demo mode)
  // Also check if we're in development mode
  const [isDemoMode, setIsDemoMode] = useState(() => {
    // In development, always default to demo mode
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      return true;
    }
    
    const savedMode = localStorage.getItem('demoMode');
    return savedMode !== null ? savedMode === 'true' : true;
  });

  // Update localStorage when the mode changes
  useEffect(() => {
    try {
      localStorage.setItem('demoMode', isDemoMode.toString());
    } catch (error) {
      console.warn('Failed to save demo mode to localStorage:', error);
    }
  }, [isDemoMode]);

  // Listen for custom events from the toggle component
  useEffect(() => {
    const handleDemoModeChange = (event: CustomEvent) => {
      setIsDemoMode(event.detail.isDemoMode);
    };

    window.addEventListener('demoModeChange', handleDemoModeChange as EventListener);
    
    return () => {
      window.removeEventListener('demoModeChange', handleDemoModeChange as EventListener);
    };
  }, []);

  return (
    <DemoModeContext.Provider value={{ isDemoMode, setIsDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoMode = (): DemoModeContextType => {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
};